import React from 'react';
import { connect } from 'react-redux';

import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import ProductReleaseTable from '../components/productReleaseTable';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import ProductReleaseEditDialog from '../containers/productReleaseEditDialog';
import ProductReleaseHistoryDialog from '../containers/productReleaseHistoryDialog';
import * as productReleasesActions from '../store/productReleasesSlice';
import { productReleasesSelectors } from '../store/productReleasesSlice';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';
import './common.css';
import setTitleToFilters from '../common/filtersTitles';
import ModalMessage from './modalMessage';

const thatObjectName = 'productReleases';

class ProductReleasesContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showProductReleaseEditDialog: false,
      productReleaseEditDialogMode: ProductReleaseEditDialog.Mode.Create,
      showHistoryDialog: false
    };
  }

  componentDidMount() {
    setTitleToFilters();
    this.props.fetchProducts();
    this.props.fetchProductReleases();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>Product releases</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderProductReleaseEditDialog()}
          {this.renderHistoryDialog()}
          <ModalMessage />
        </div>
      </OverlayLoader>
    );
  }

  renderButtons = () => (
    <TooltipButtonGroup>
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.refreshProductReleases}/>
      <TooltipElement type="button" text="Create product release" icon="plus" onClick={this.createNewProductRelease} displayOnlyForWriter />
      <TooltipElement type="button" text="Edit product release" icon="edit" onClick={this.editProductRelease}
                         disabled={this.isEditButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Show product release history" icon="list-alt" onClick={this.showProductReleaseHistory}
                         disabled={this.isHistoryButtonDisabled()}/>
    </TooltipButtonGroup>
  );

  isEditButtonDisabled = () => this.props.selectedProductReleases.length !== 1;

  isHistoryButtonDisabled = () => this.props.selectedProductReleases.length !== 1;

  showProductReleaseHistory = () => {
    this.setState({ showHistoryDialog: true });
  };

  closeHistoryDialog = () => {
    this.setState({ showHistoryDialog: false });
  };

  getActionsForFavorites = () => ({
    addToFavorites: this.props.addProductReleaseToFavorites,
    removeFromFavorites: this.props.removeProductReleaseFromFavorites,
  });

  renderContent = () => {
    if (this.props.productReleasesFetchingError)
      return this.renderError(this.props.productReleasesFetchingError.toString());
    return <React.Fragment>
      {this.props.productsState.productsError ? this.renderError(this.props.productsState.productsError.toString()) : null}
      <TableSelectionManager
        items={this.props.productReleases}
        objectName={thatObjectName}
      >
        <ProductReleaseTable
          products={this.props.productsState.data}
          productReleases={this.props.productReleases}
          actions={this.getActionsForFavorites()}
        />
      </TableSelectionManager>
    </React.Fragment>
  };

  renderError = message => (
    <p className="text-danger">Error: {message}</p>
  );

  isLoading = () => this.props.isProductReleasesFetching
    || this.props.productsState.isProductsFetching
    || this.props.isProductReleasesUpdating;

  getLoaderMessage = () => {
    if (this.props.isProductReleasesFetching)
      return "Product releases loading...";
    if (this.props.productsState.isProductsFetching)
      return "Products loading...";
    if (this.props.isProductReleasesUpdating)
      return "Products updating...";
  };

  renderProductReleaseEditDialog = () => (
    <ProductReleaseEditDialog
      mode={this.state.productReleaseEditDialogMode}
      onClose={this.closeProductReleaseEditDialog}
      show={this.state.showProductReleaseEditDialog}
      products={this.props.productsState.data.map(product => product.name)}
      id={this.getSelectedProductReleaseFieldValue('id')}
      product={this.getSelectedProductReleaseFieldValue('productName')}
      version={this.getSelectedProductReleaseFieldValue('productVersion')}
      releaseDate={this.getSelectedProductReleaseFieldValue('releaseDate')}
    />
  );

  renderHistoryDialog = () => (
    <ProductReleaseHistoryDialog
      show={this.state.showHistoryDialog}
      onClose={this.closeHistoryDialog}
      productVersion={this.getSelectedProductReleaseFieldValue('productVersion')}
      productName={this.getSelectedProductReleaseFieldValue('productName')}
      id={this.getSelectedProductReleaseFieldValue('id')}
    />
  );

  getSelectedProductReleaseFieldValue = (fieldName, defaultValue = null) => {
    const productRelease = this.props.selectedProductReleases[0];
    try {
      const value = fieldName.split('.').reduce((acc, key) => acc[key], productRelease);
      return value === undefined ? defaultValue : value === null ? defaultValue : value;
    } catch (e) {
      return defaultValue;
    }
  };

  refreshProductReleases = () => {
    this.props.fetchProductReleases();
  };

  createNewProductRelease = () => {
    this.setState({
      productReleaseEditDialogMode: ProductReleaseEditDialog.Mode.Create,
      showProductReleaseEditDialog: true,
    });
  };

  editProductRelease = () => {
    this.setState({
      productReleaseEditDialogMode: ProductReleaseEditDialog.Mode.Edit,
      showProductReleaseEditDialog: true
    });
  };

  closeProductReleaseEditDialog = () => {
    this.setState({ showProductReleaseEditDialog: false });
  };

}

const mapStateToProps = (state) => ({
  productsState: objectDataSelectors.productsState(state),

  isProductReleasesFetching: productReleasesSelectors.isFetching(state),
  isProductReleasesUpdating: productReleasesSelectors.isUpdating(state),
  productReleases: productReleasesSelectors.selectAll(state),
  productReleasesFetchingError: productReleasesSelectors.fetchingError(state),
  selectedProductReleases: productReleasesSelectors.selectedProductReleases(state),
});

const actionCreators = {
  fetchProductReleases: productReleasesActions.fetchAll,
  fetchProducts: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.products }),
  addProductReleaseToFavorites: productReleasesActions.addToFavorites,
  removeProductReleaseFromFavorites: productReleasesActions.removeFromFavorites,
};

export default connect(mapStateToProps, actionCreators)(ProductReleasesContainer);



// WEBPACK FOOTER //
// ./src/containers/productReleasesContainer.js